import React, { useEffect, useState } from 'react';
import { useProdAuth } from '../../contexts/ProdAuthContext';

const ACCENT = '#4A7C59';

interface ProdUserRecord {
  id: string;
  name: string;
  email: string;
  role: string;
  active: boolean;
  invitePending: boolean;
  lastLogin: string | null;
  createdAt: string;
  centreName: string | null;
  collectionCentreId: string | null;
}

interface Centre {
  id: string;
  name: string;
}

const ROLE_BADGE: Record<string, string> = {
  field: 'bg-blue-100 text-blue-700',
  transport: 'bg-amber-100 text-amber-700',
  administration: 'bg-purple-100 text-purple-700',
  admin: 'bg-red-100 text-red-700',
  super_admin: 'bg-green-100 text-green-700',
};

const ROLE_LABELS: Record<string, string> = {
  field: 'Field',
  transport: 'Transport',
  administration: 'Administration',
  admin: 'Admin',
  super_admin: 'Super Admin',
};

const ROLE_DESCRIPTIONS: Record<string, string> = {
  field: 'Manages station deliveries and farmers',
  transport: 'Handles trips from stations to hub',
  administration: 'Manages processing, drying & inventory',
};

// Role order for grouping
const ROLE_ORDER = ['field', 'transport', 'administration', 'admin', 'super_admin'];

// Roles available to admin (Florence) when creating/editing staff
const STAFF_ROLES = ['field', 'transport', 'administration'];
// Super admin can also set admin
const ALL_ROLES = ['field', 'transport', 'administration', 'admin', 'super_admin'];

export const UserList: React.FC = () => {
  const { user: currentUser } = useProdAuth();
  const [users, setUsers] = useState<ProdUserRecord[]>([]);
  const [centres, setCentres] = useState<Centre[]>([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [inviteMode, setInviteMode] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'field', collectionCentreId: '' });

  const [editUser, setEditUser] = useState<ProdUserRecord | null>(null);
  const [editForm, setEditForm] = useState({ name: '', role: '', collectionCentreId: '', newPassword: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState<Record<string, boolean>>({});
  const [resendSent, setResendSent] = useState<Record<string, boolean>>({});
  const [actionError, setActionError] = useState<Record<string, string>>({});

  const availableRoles = currentUser?.role === 'super_admin' ? ALL_ROLES : STAFF_ROLES;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, centresRes] = await Promise.all([
        fetch('/api/production/users', { credentials: 'include' }),
        fetch('/api/production/collection-centres', { credentials: 'include' }),
      ]);
      const [usersData, centresData] = await Promise.all([usersRes.json(), centresRes.json()]);
      setUsers(usersData.users || []);
      setCentres(centresData.centres || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      const body = inviteMode
        ? { name: newUser.name, email: newUser.email, role: newUser.role, collectionCentreId: newUser.collectionCentreId, invite: true }
        : newUser;
      const res = await fetch('/api/production/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) { setFormError(d.error || 'Failed to create user'); return; }
      setShowForm(false);
      setNewUser({ name: '', email: '', password: '', role: 'field', collectionCentreId: '' });
      setInviteMode(false);
      fetchData();
      if (d.emailError) {
        alert(`Staff member added, but the welcome email failed:\n\n${d.emailError}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setInviteMode(false);
    setFormError('');
    setNewUser({ name: '', email: '', password: '', role: 'field', collectionCentreId: '' });
  };

  const openEdit = (u: ProdUserRecord) => {
    setEditUser(u);
    setEditForm({ name: u.name, role: u.role, collectionCentreId: u.collectionCentreId || '', newPassword: '' });
    setEditError('');
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editUser) return;
    setEditSaving(true);
    setEditError('');
    try {
      const body: any = {
        name: editForm.name,
        role: editForm.role,
        collectionCentreId: editForm.collectionCentreId || null,
      };
      if (editForm.newPassword) {
        if (editForm.newPassword.length < 8) { setEditError('New password must be at least 8 characters.'); return; }
        body.password = editForm.newPassword;
      }
      const res = await fetch(`/api/production/users?id=${editUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) { const d = await res.json(); setEditError(d.error || 'Failed to save'); return; }
      setEditUser(null);
      fetchData();
    } finally {
      setEditSaving(false);
    }
  };

  const toggleActive = async (u: ProdUserRecord) => {
    await fetch(`/api/production/users?id=${u.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ active: !u.active }),
    });
    setUsers(us => us.map(x => x.id === u.id ? { ...x, active: !u.active } : x));
  };

  const handleDelete = async (id: string) => {
    const res = await fetch(`/api/production/users?id=${id}`, { method: 'DELETE', credentials: 'include' });
    if (res.ok) {
      setUsers(us => us.filter(u => u.id !== id));
      setConfirmDeleteId(null);
    } else {
      const d = await res.json();
      setActionError(prev => ({ ...prev, [id]: d.error || 'Failed to delete' }));
      setConfirmDeleteId(null);
    }
  };

  const handleResend = async (u: ProdUserRecord) => {
    setResendLoading(prev => ({ ...prev, [u.id]: true }));
    setActionError(prev => ({ ...prev, [u.id]: '' }));
    try {
      const res = await fetch(`/api/production/users?action=resend&id=${u.id}`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        setResendSent(prev => ({ ...prev, [u.id]: true }));
        setTimeout(() => setResendSent(prev => ({ ...prev, [u.id]: false })), 3000);
      } else {
        const d = await res.json();
        setActionError(prev => ({ ...prev, [u.id]: d.error || 'Failed to send' }));
      }
    } finally {
      setResendLoading(prev => ({ ...prev, [u.id]: false }));
    }
  };

  // Group by role
  const grouped = ROLE_ORDER.reduce<Record<string, ProdUserRecord[]>>((acc, role) => {
    const inRole = users.filter(u => u.role === role);
    if (inRole.length > 0) acc[role] = inRole;
    return acc;
  }, {});

  const inputCls = "w-full px-4 py-2.5 border border-[#E8E6E2] rounded-lg text-sm text-[#1A1A1A] bg-white focus:outline-none";
  const foc = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => (e.target.style.boxShadow = `0 0 0 2px ${ACCENT}33`);
  const blu = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => (e.target.style.boxShadow = '');

  const activeCount = users.filter(u => u.active).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#1A1A1A]">Hub Staff</h2>
          <p className="text-sm text-[#6B6B6B] mt-0.5">{users.length} members · {activeCount} active</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2.5 text-sm font-medium text-white rounded-lg"
          style={{ backgroundColor: ACCENT }}
        >
          + Add staff member
        </button>
      </div>

      {/* ── Create modal ─────────────────────────────────────────────────── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-[#1A1A1A]">Add staff member</h3>
              <button onClick={handleCloseForm} className="text-[#A3A3A3] hover:text-[#1A1A1A]">✕</button>
            </div>

            {/* Invite mode toggle */}
            <div className="mb-5">
              <div className="inline-flex rounded-lg border border-[#E8E6E2] p-0.5 bg-[#FAFAF8]">
                <button type="button" onClick={() => setInviteMode(false)}
                  className="px-4 py-1.5 text-sm font-medium rounded-md transition-colors"
                  style={!inviteMode ? { backgroundColor: ACCENT, color: 'white' } : { color: '#6B6B6B' }}>
                  Set password
                </button>
                <button type="button" onClick={() => setInviteMode(true)}
                  className="px-4 py-1.5 text-sm font-medium rounded-md transition-colors"
                  style={inviteMode ? { backgroundColor: ACCENT, color: 'white' } : { color: '#6B6B6B' }}>
                  Send invite link
                </button>
              </div>
              {inviteMode && (
                <p className="mt-2 text-xs text-[#6B6B6B]">An invite link will be emailed to them to set their own password.</p>
              )}
            </div>

            {formError && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{formError}</div>}

            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">Full name</label>
                <input type="text" value={newUser.name} onChange={e => setNewUser(u => ({ ...u, name: e.target.value }))}
                  placeholder="e.g. Anthony Ochieng" required className={inputCls} onFocus={foc} onBlur={blu} />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">Email</label>
                <input type="email" value={newUser.email} onChange={e => setNewUser(u => ({ ...u, email: e.target.value }))}
                  placeholder="email@example.com" required className={inputCls} onFocus={foc} onBlur={blu} />
              </div>

              {!inviteMode && (
                <div>
                  <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">Temporary password</label>
                  <input type="password" value={newUser.password} onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))}
                    placeholder="Min 8 characters" required className={inputCls} onFocus={foc} onBlur={blu} />
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">Role</label>
                <select value={newUser.role} onChange={e => setNewUser(u => ({ ...u, role: e.target.value }))}
                  className={inputCls} onFocus={foc} onBlur={blu}>
                  {availableRoles.map(r => <option key={r} value={r}>{ROLE_LABELS[r] ?? r}</option>)}
                </select>
                {ROLE_DESCRIPTIONS[newUser.role] && (
                  <p className="mt-1 text-xs text-[#A3A3A3]">{ROLE_DESCRIPTIONS[newUser.role]}</p>
                )}
              </div>

              {centres.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">
                    Assigned centre <span className="text-[#A3A3A3] normal-case font-normal">— optional</span>
                  </label>
                  <select value={newUser.collectionCentreId} onChange={e => setNewUser(u => ({ ...u, collectionCentreId: e.target.value }))}
                    className={inputCls} onFocus={foc} onBlur={blu}>
                    <option value="">No specific centre</option>
                    {centres.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={handleCloseForm}
                  className="flex-1 py-2.5 text-sm text-[#6B6B6B] border border-[#E8E6E2] rounded-lg hover:bg-[#F0EDE8]">
                  Cancel
                </button>
                <button type="submit" disabled={saving}
                  className="flex-1 py-2.5 text-sm font-medium text-white rounded-lg disabled:opacity-50"
                  style={{ backgroundColor: ACCENT }}>
                  {saving ? (inviteMode ? 'Sending...' : 'Adding...') : (inviteMode ? 'Send invite' : 'Add member')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Edit modal ───────────────────────────────────────────────────── */}
      {editUser && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-lg font-bold text-[#1A1A1A]">{editUser.name}</h3>
                <p className="text-xs text-[#A3A3A3] mt-0.5">{editUser.email}</p>
              </div>
              <button onClick={() => setEditUser(null)} className="text-[#A3A3A3] hover:text-[#1A1A1A]">✕</button>
            </div>

            {editError && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{editError}</div>}

            <form onSubmit={handleEditSave} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">Full name</label>
                <input type="text" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                  required className={inputCls} onFocus={foc} onBlur={blu} />
              </div>

              <div>
                <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">Role</label>
                <select value={editForm.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}
                  className={inputCls} onFocus={foc} onBlur={blu}
                  disabled={editUser.id === currentUser?.id}>
                  {availableRoles.map(r => <option key={r} value={r}>{ROLE_LABELS[r] ?? r}</option>)}
                </select>
                {editUser.id === currentUser?.id && (
                  <p className="mt-1 text-xs text-[#A3A3A3]">You cannot change your own role.</p>
                )}
              </div>

              {centres.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">
                    Assigned centre <span className="text-[#A3A3A3] normal-case font-normal">— optional</span>
                  </label>
                  <select value={editForm.collectionCentreId} onChange={e => setEditForm(f => ({ ...f, collectionCentreId: e.target.value }))}
                    className={inputCls} onFocus={foc} onBlur={blu}>
                    <option value="">No specific centre</option>
                    {centres.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">
                  New password <span className="text-[#A3A3A3] normal-case font-normal">— leave blank to keep current</span>
                </label>
                <input type="password" value={editForm.newPassword}
                  onChange={e => setEditForm(f => ({ ...f, newPassword: e.target.value }))}
                  placeholder="Enter new password"
                  className={inputCls} onFocus={foc} onBlur={blu} />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditUser(null)}
                  className="flex-1 py-2.5 text-sm text-[#6B6B6B] border border-[#E8E6E2] rounded-lg hover:bg-[#F0EDE8]">
                  Cancel
                </button>
                <button type="submit" disabled={editSaving}
                  className="flex-1 py-2.5 text-sm font-medium text-white rounded-lg disabled:opacity-50"
                  style={{ backgroundColor: ACCENT }}>
                  {editSaving ? 'Saving...' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Staff list ───────────────────────────────────────────────────── */}
      {loading ? (
        <div className="py-16 text-center text-sm text-[#6B6B6B]">Loading...</div>
      ) : users.length === 0 ? (
        <div className="py-16 text-center text-sm text-[#6B6B6B]">No staff added yet.</div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([role, members]) => (
            <section key={role}>
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_BADGE[role] || 'bg-neutral-100 text-neutral-600'}`}>
                  {ROLE_LABELS[role] ?? role}
                </span>
                <span className="text-xs text-[#A3A3A3]">{members.length} member{members.length !== 1 ? 's' : ''}</span>
                {ROLE_DESCRIPTIONS[role] && (
                  <span className="text-xs text-[#A3A3A3]">· {ROLE_DESCRIPTIONS[role]}</span>
                )}
              </div>

              <div className="bg-white border border-[#E8E6E2] rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <tbody>
                    {members.map((u, i) => (
                      <tr key={u.id} className={`border-b border-[#E8E6E2] last:border-0 ${i % 2 === 0 ? '' : 'bg-[#FAFAF8]'}`}>
                        {/* Name + email */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                              style={{ backgroundColor: ACCENT }}
                            >
                              {u.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-[#1A1A1A]">{u.name}</p>
                              <p className="text-xs text-[#A3A3A3]">{u.email}</p>
                            </div>
                          </div>
                        </td>

                        {/* Centre assignment */}
                        <td className="px-4 py-3 hidden md:table-cell">
                          {u.centreName
                            ? <span className="text-xs text-[#6B6B6B]">{u.centreName}</span>
                            : <span className="text-xs text-[#A3A3A3]">—</span>}
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            u.invitePending
                              ? 'bg-amber-100 text-amber-700'
                              : u.active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-neutral-100 text-neutral-500'
                          }`}>
                            {u.invitePending ? 'Invite pending' : u.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>

                        {/* Last login */}
                        <td className="px-4 py-3 text-xs text-[#A3A3A3] hidden lg:table-cell">
                          {u.invitePending ? '—' : u.lastLogin
                            ? new Date(u.lastLogin).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' })
                            : 'Never logged in'}
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3 text-xs whitespace-nowrap justify-end">
                            <button onClick={() => openEdit(u)} className="text-[#6B6B6B] hover:text-[#1A1A1A] transition-colors">
                              Edit
                            </button>

                            <button
                              onClick={() => handleResend(u)}
                              disabled={!!resendLoading[u.id]}
                              className="transition-colors disabled:opacity-50"
                              style={{ color: resendSent[u.id] ? ACCENT : '#6B6B6B' }}
                              onMouseEnter={e => { if (!resendSent[u.id]) (e.target as HTMLElement).style.color = '#1A1A1A'; }}
                              onMouseLeave={e => { if (!resendSent[u.id]) (e.target as HTMLElement).style.color = '#6B6B6B'; }}
                            >
                              {resendLoading[u.id] ? 'Sending…' : resendSent[u.id] ? '✓ Sent' : u.invitePending ? 'Resend invite' : 'Send reset'}
                            </button>

                            {u.id !== currentUser?.id && (
                              <>
                                <button onClick={() => toggleActive(u)} className="text-[#6B6B6B] hover:text-[#1A1A1A] transition-colors">
                                  {u.active ? 'Deactivate' : 'Activate'}
                                </button>

                                {currentUser?.role === 'super_admin' && (
                                  confirmDeleteId === u.id ? (
                                    <span className="flex items-center gap-1.5">
                                      <span className="text-[#A3A3A3]">Delete?</span>
                                      <button onClick={() => handleDelete(u.id)} className="text-red-600 hover:text-red-700 font-semibold transition-colors">Yes</button>
                                      <button onClick={() => setConfirmDeleteId(null)} className="text-[#6B6B6B] hover:text-[#1A1A1A] transition-colors">No</button>
                                    </span>
                                  ) : (
                                    <button onClick={() => setConfirmDeleteId(u.id)} className="text-red-400 hover:text-red-600 transition-colors">
                                      Delete
                                    </button>
                                  )
                                )}
                              </>
                            )}

                            {actionError[u.id] && <span className="text-red-500">{actionError[u.id]}</span>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};
